import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  APIProvider,
  Circle,
  ControlPosition,
  Map as GoogleMap,
  MapControl,
  Polygon,
  useMap,
} from "@vis.gl/react-google-maps";
import {
  TerraDraw,
  TerraDrawFreehandMode,
  TerraDrawPolygonMode,
  ValidateNotSelfIntersecting,
} from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";
import {
  MdGesture,
  MdOutlinePolyline,
  MdOutlineTouchApp,
} from "react-icons/md";

import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import { areaAt, areasFor, areasIn, boundsOf } from "./postalAreas";
import { googleMapsKey } from "./routeSheet";

const MAP_ID = "delivery-route-map";
const DEFAULT_CENTER = { lat: 45.508888, lng: -73.561668 };
const COORDINATE_PRECISION = 6;
const FIT_PADDING = 48;
const DATA_LICENCE_URL =
  "https://open.canada.ca/en/open-government-licence-canada";

const NEARBY_COLOR = "#64748b";
const LABEL_HALO = "0 0 2px #fff, 0 0 2px #fff, 0 0 3px #fff";
const NEARBY_MIN_ZOOM = 10;
const LABEL_MIN_WIDTH = 32;
const LABEL_MIN_HEIGHT = 16;
const NAME_MAX_WIDTH = 160;
const NAME_HALF_HEIGHT = 10;
const NOTICE_MS = 4000;

const TOOLS = {
  freehand: { label: "Draw", icon: MdGesture },
  polygon: { label: "Corners", icon: MdOutlinePolyline },
};

const NOTICES = {
  crossed: "Outline crossed itself. Try again.",
  empty: "No postal areas there.",
};

const isTouch = () => !!window.matchMedia?.("(pointer: coarse)").matches;

const stepText = (mode, touch) =>
  mode === "freehand"
    ? "Drag around the areas"
    : `${touch ? "Tap" : "Click"} corners, then the first one`;

const notSelfIntersecting = (feature, { updateType }) =>
  updateType === "provisional"
    ? { valid: true }
    : ValidateNotSelfIntersecting(feature);

const drawableColor = (color, fallback) =>
  /^#[0-9a-f]{6}$/i.test(color) ? color : fallback;

const tokenHex = (name) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const fitsLabel = ({ box }, zoom) => {
  const pixelsPerDegree = (256 * 2 ** zoom) / 360;
  const midLat = (((box.north + box.south) / 2) * Math.PI) / 180;
  return (
    (box.east - box.west) * pixelsPerDegree >= LABEL_MIN_WIDTH &&
    ((box.north - box.south) * pixelsPerDegree) / Math.cos(midLat) >=
      LABEL_MIN_HEIGHT
  );
};

const anchorOf = (areas) => {
  const lat = areas.reduce((sum, area) => sum + area.label.lat, 0) / areas.length;
  const lng = areas.reduce((sum, area) => sum + area.label.lng, 0) / areas.length;
  const distance = ({ label }) => (label.lat - lat) ** 2 + (label.lng - lng) ** 2;
  return areas.reduce((best, area) =>
    distance(area) < distance(best) ? area : best,
  ).label;
};

function createDraw(map, color) {
  const shape = {
    fillColor: color,
    fillOpacity: 0.2,
    outlineColor: color,
    outlineWidth: 2,
  };

  return new TerraDraw({
    adapter: new TerraDrawGoogleMapsAdapter({
      lib: window.google.maps,
      map,
      coordinatePrecision: COORDINATE_PRECISION,
      isolatedData: true,
    }),
    modes: [
      new TerraDrawFreehandMode({
        styles: shape,
        drawInteraction: "click-drag",
        smoothing: 0.5,
        autoClose: true,
      }),
      new TerraDrawPolygonMode({ styles: shape, validation: notSelfIntersecting }),
    ],
  });
}

function DrawLayer({ color, mode, onDraw, onDone }) {
  const map = useMap();
  const drawRef = useRef(null);
  const handlers = useRef({ onDraw, onDone });
  handlers.current = { onDraw, onDone };
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!map) return undefined;
    let draw;

    const start = () => {
      draw = createDraw(map, color);
      drawRef.current = draw;

      draw.on("ready", () => {
        draw.setMode("static");
        setReady(true);
      });

      draw.on("finish", (id, { mode: finishedMode, action }) => {
        if (action !== "draw") return;
        if (finishedMode === "freehand") map.setOptions({ draggable: true });
        const feature = draw.getSnapshotFeature(id);
        draw.removeFeatures([id]);
        if (!ValidateNotSelfIntersecting(feature).valid) {
          handlers.current.onDone("crossed");
          return;
        }
        const added = handlers.current.onDraw(feature.geometry);
        handlers.current.onDone(added ? null : "empty");
      });

      draw.start();
    };

    let listener;
    if (map.getProjection()) start();
    else listener = map.addListener("projection_changed", () => {
      listener.remove();
      start();
    });

    return () => {
      listener?.remove();
      draw?.stop();
      drawRef.current = null;
      requestAnimationFrame(() =>
        map.data.forEach((feature) => map.data.remove(feature)),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (ready) drawRef.current?.setMode(mode);
  }, [mode, ready]);

  return null;
}

function MapLabels({ labels }) {
  const map = useMap();
  const [overlay, setOverlay] = useState(null);
  const [, setFrame] = useState(0);

  useEffect(() => {
    if (!map) return undefined;
    const view = new window.google.maps.OverlayView();
    const container = document.createElement("div");
    view.onAdd = () => view.getPanes().overlayLayer.appendChild(container);
    view.draw = () => setFrame((frame) => frame + 1);
    view.onRemove = () => container.remove();
    view.setMap(map);
    setOverlay({ view, container });
    return () => view.setMap(null);
  }, [map]);

  const projection = overlay?.view.getProjection();
  if (!projection) return null;

  const placed = [];
  const fitsName = (text, point) => {
    const halfWidth = Math.min(text.length * 7 + 16, NAME_MAX_WIDTH) / 2;
    const box = {
      left: point.x - halfWidth,
      right: point.x + halfWidth,
      top: point.y - NAME_HALF_HEIGHT,
      bottom: point.y + NAME_HALF_HEIGHT,
    };
    const clashes = placed.some(
      (other) =>
        other.left < box.right &&
        box.left < other.right &&
        other.top < box.bottom &&
        box.top < other.bottom,
    );
    if (!clashes) placed.push(box);
    return !clashes;
  };

  const shown = labels
    .map((label) => ({
      ...label,
      point: projection.fromLatLngToDivPixel(label.position),
    }))
    .filter(
      (label) => label.variant !== "name" || fitsName(label.text, label.point),
    );

  return createPortal(
    shown.map(({ key, text, point, color, variant }) =>
      variant === "name" ? (
        <span
          key={key}
          className="pointer-events-none absolute max-w-[160px] -translate-x-1/2 -translate-y-1/2 truncate rounded-full px-[8px] py-[2px] font-manrope text-[11.5px] font-bold text-white shadow-ui-md"
          style={{ left: point.x, top: point.y, background: color }}
        >
          {text}
        </span>
      ) : (
        <span
          key={key}
          className={`pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-manrope text-[10.5px] tracking-[0.3px] ${
            variant === "strong" ? "font-extrabold" : "font-semibold"
          }`}
          style={{ left: point.x, top: point.y, color, textShadow: LABEL_HALO }}
        >
          {text}
        </span>
      ),
    ),
    overlay.container,
  );
}

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (map && bounds) map.fitBounds(bounds, FIT_PADDING);
  }, [map, bounds]);
  return null;
}

function Notice({ notice }) {
  if (!notice) return null;
  return (
    <p
      role="alert"
      className="m-[0px] rounded-[6px] bg-state-warning-bg px-[8px] py-[5px] text-[11.5px] font-semibold leading-[1.4] text-state-warning-text"
    >
      {NOTICES[notice]}
    </p>
  );
}

export default function RouteMap({
  areas = [],
  routes = [],
  draft = null,
  focus = null,
  preview = null,
  hovered = null,
  activeRouteId = null,
  onSelectRoute,
  onHoverRoute,
  onDraw,
  onToggleArea,
  onHover,
}) {
  const brand = tokenHex("--color-brand");
  const touch = isTouch();
  const tools = touch ? ["polygon"] : ["freehand", "polygon"];
  const editing = !!draft;
  const draftId = draft?.id ?? null;
  const draftColor = drawableColor(draft?.color, brand);
  const draftPrefixes = draft?.prefixes;
  const empty = editing && !draftPrefixes.length;
  const [view, setView] = useState(null);
  const [armed, setArmed] = useState(null);
  const [notice, setNotice] = useState(null);
  const interactive = editing && !armed;

  useEffect(() => {
    if (editing) return;
    setArmed(null);
    setNotice(null);
  }, [editing]);

  useEffect(() => {
    if (armed) onHover?.(null);
  }, [armed, onHover]);

  useEffect(() => {
    if (!armed) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setArmed(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [armed]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const arm = (tool) => {
    setNotice(null);
    setArmed(tool);
  };

  const routeShapes = useMemo(
    () =>
      routes
        .filter((route) => route.id !== draftId)
        .map((route) => ({
          routeId: route.id,
          name: route.name,
          color: drawableColor(route.color, brand),
          areas: areasFor(areas, route.prefixes),
        }))
        .filter((shape) => shape.areas.length)
        .map((shape) => ({ ...shape, anchor: anchorOf(shape.areas) })),
    [areas, routes, draftId, brand],
  );

  const selected = useMemo(
    () => (draftPrefixes ? areasFor(areas, draftPrefixes) : []),
    [areas, draftPrefixes],
  );

  const previewAreas = useMemo(() => {
    if (!preview) return [];
    const chosen = new Set(selected.map((area) => area.code));
    return areasFor(areas, preview.codes).filter(
      (area) => !chosen.has(area.code),
    );
  }, [preview, areas, selected]);

  const nearby = useMemo(() => {
    if (!editing || !view || view.zoom < NEARBY_MIN_ZOOM) return [];
    const chosen = new Set(selected.map((area) => area.code));
    return areasIn(areas, view.box).filter((area) => !chosen.has(area.code));
  }, [editing, view, areas, selected]);

  const labels = useMemo(() => {
    if (!view) return [];
    const byCode = new Map();
    const add = (list, color, variant) =>
      areasIn(list, view.box)
        .filter((area) => fitsLabel(area, view.zoom))
        .forEach((area) => {
          if (!byCode.has(area.code)) {
            byCode.set(area.code, {
              key: area.code,
              text: area.code,
              position: area.label,
              color,
              variant,
            });
          }
        });
    if (editing) {
      add(selected, draftColor, "strong");
      add(nearby, NEARBY_COLOR, "faint");
    } else {
      routeShapes.forEach((shape) => add(shape.areas, shape.color, "strong"));
    }
    const names = routeShapes
      .map((shape) => ({
        key: `route-${shape.routeId}`,
        text: shape.name,
        position: shape.anchor,
        color: shape.color,
        variant: "name",
        active: shape.routeId === activeRouteId,
      }))
      .sort((a, b) => b.active - a.active);
    return [...byCode.values(), ...names];
  }, [view, editing, selected, nearby, routeShapes, draftColor, activeRouteId]);

  const [initialBounds] = useState(() =>
    boundsOf(
      areasFor(
        areas,
        routes.flatMap((route) => route.prefixes),
      ),
    ),
  );

  const isHovered = (area) => !!hovered && area.code.startsWith(hovered);
  const areaEvents = (area) =>
    interactive
      ? {
          onClick: () => onToggleArea?.(area.code),
          onMouseOver: () => onHover?.(area.code),
          onMouseOut: () => onHover?.(null),
        }
      : {};

  const ArmedIcon = armed ? TOOLS[armed].icon : null;

  return (
    <APIProvider apiKey={googleMapsKey()}>
      <GoogleMap
        id={MAP_ID}
        className="h-full w-full"
        {...(initialBounds
          ? { defaultBounds: { ...initialBounds, padding: FIT_PADDING } }
          : { defaultCenter: DEFAULT_CENTER, defaultZoom: 11 })}
        gestureHandling={armed === "freehand" ? "none" : "greedy"}
        disableDoubleClickZoom={editing}
        clickableIcons={false}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        onClick={(event) => {
          if (!interactive || !event.detail.latLng) return;
          const area = areaAt(areas, event.detail.latLng);
          if (area) onToggleArea?.(area.code);
        }}
        onIdle={({ map }) => {
          const bounds = map.getBounds();
          if (bounds) setView({ zoom: map.getZoom(), box: bounds.toJSON() });
        }}
      >
        {routeShapes.map((shape) => {
          const active = shape.routeId === activeRouteId;
          const faded = activeRouteId != null && !active;
          return (
            <Polygon
              key={shape.routeId}
              paths={shape.areas.flatMap((area) => area.paths)}
              clickable={!editing}
              onClick={() => onSelectRoute?.(shape.routeId)}
              onMouseOver={() => !editing && onHoverRoute?.(shape.routeId)}
              onMouseOut={() => !editing && onHoverRoute?.(null)}
              strokeColor={shape.color}
              strokeOpacity={faded ? 0.35 : 0.9}
              strokeWeight={active ? 3 : 1}
              fillColor={shape.color}
              fillOpacity={active ? 0.35 : faded ? 0.06 : 0.15}
              zIndex={active ? 1 : 0}
            />
          );
        })}
        {nearby.map((area) => (
          <Polygon
            key={`nearby-${area.code}`}
            paths={area.paths}
            clickable={interactive}
            {...areaEvents(area)}
            strokeColor={NEARBY_COLOR}
            strokeOpacity={0.55}
            strokeWeight={1}
            fillColor={draftColor}
            fillOpacity={isHovered(area) ? 0.15 : 0}
            zIndex={1}
          />
        ))}
        {selected.map((area) => (
          <Polygon
            key={`selected-${area.code}`}
            paths={area.paths}
            clickable={interactive}
            {...areaEvents(area)}
            strokeColor={draftColor}
            strokeOpacity={1}
            strokeWeight={isHovered(area) ? 3 : 1.5}
            fillColor={draftColor}
            fillOpacity={isHovered(area) ? 0.5 : 0.3}
            zIndex={2}
          />
        ))}
        {previewAreas.map((area) => (
          <Polygon
            key={`preview-${area.code}`}
            paths={area.paths}
            clickable={false}
            strokeColor={draftColor}
            strokeOpacity={0.7}
            strokeWeight={1}
            fillColor={draftColor}
            fillOpacity={0.15}
            zIndex={2}
          />
        ))}
        {preview && (
          <Circle
            center={preview.center}
            radius={preview.km * 1000}
            clickable={false}
            strokeColor={draftColor}
            strokeOpacity={0.9}
            strokeWeight={2}
            fillOpacity={0}
            zIndex={3}
          />
        )}
        <MapLabels labels={labels} />
        <FitBounds bounds={focus} />
        <MapControl position={ControlPosition.LEFT_BOTTOM}>
          <a
            href={DATA_LICENCE_URL}
            target="_blank"
            rel="noreferrer"
            className="block bg-ui-surface px-[5px] font-manrope text-[10px] leading-[14px] text-ui-text-muted no-underline opacity-80 hover:text-ui-text-muted hover:underline"
          >
            Postal areas: StatCan (OGL)
          </a>
        </MapControl>
        {editing && (
          <DrawLayer
            key={draftId ?? "new"}
            color={draftColor}
            mode={armed ?? "static"}
            onDraw={onDraw}
            onDone={(result) => {
              setArmed(null);
              setNotice(result);
            }}
          />
        )}
        {editing && !armed && (
          <MapControl position={ControlPosition.TOP_LEFT}>
            <div className="m-[8px] flex max-w-[380px] flex-col gap-[6px] rounded-[9px] border-[1px] border-ui-border bg-ui-surface p-[6px] font-manrope shadow-ui-md">
              <div className="flex items-center gap-[6px]">
                {tools.map((tool, index) => (
                  <BoltButton
                    key={tool}
                    type="button"
                    variant={empty && index === 0 ? "primary" : "outline"}
                    size="sm"
                    icon={TOOLS[tool].icon}
                    label={TOOLS[tool].label}
                    onClick={() => arm(tool)}
                  />
                ))}
                <span className="ml-[2px] flex items-center gap-[5px] self-stretch border-l-[1px] border-ui-border pl-[8px] pr-[4px] text-[11px] font-medium leading-[1.3] text-ui-text-muted">
                  <MdOutlineTouchApp className="h-[15px] w-[15px] shrink-0" />
                  or {touch ? "tap" : "click"} areas to add/remove
                </span>
              </div>
              <Notice notice={notice} />
            </div>
          </MapControl>
        )}
        {armed && (
          <MapControl position={ControlPosition.TOP_CENTER}>
            <div
              role="status"
              className="mt-[8px] flex items-center gap-[8px] rounded-full border-[1px] border-brand bg-ui-surface py-[5px] pl-[12px] pr-[5px] font-manrope text-[12px] font-semibold text-ui-text shadow-ui-md"
            >
              <ArmedIcon className="h-[16px] w-[16px] shrink-0 text-brand" />
              {stepText(armed, touch)}
              <BoltButton
                type="button"
                variant="outline"
                size="sm"
                label="Cancel"
                onClick={() => setArmed(null)}
                className="rounded-full"
              />
            </div>
          </MapControl>
        )}
      </GoogleMap>
    </APIProvider>
  );
}
